import { useEffect, useRef, useState } from 'react';

/** How long typing must pause before the rename is sent to the server. */
export const RENAME_DEBOUNCE_MS = 500;

type RenameId = number | string;

export interface RenameQueue {
  /**
   * Record a locally-typed name: shields it from server data and schedules a
   * debounced save. Call this from the input's `onChange`, after updating the
   * component's own state.
   */
  queue: (id: RenameId, name: string) => void;
  /**
   * The name to render for `id`. Returns the user's unsaved edit when there is
   * one, otherwise the value that came back from the server.
   */
  resolve: (id: RenameId, serverName: string) => string;
  /** Send any outstanding edits immediately and cancel their timers. */
  flush: () => void;
}

/**
 * Debounces rename-as-you-type saves and protects the field from being
 * overwritten by a refetch that is already stale.
 *
 * Renaming used to PATCH on every keystroke, and each mutation invalidated the
 * list query. The refetch that followed an early keystroke would land after the
 * user had typed further characters and reset the field to the older server
 * value — the input visibly lost a letter or two, non-deterministically,
 * depending on how request timing interleaved with typing.
 *
 * Two things fix that: only save once typing pauses, and treat a local edit as
 * newer than anything the server returns until that edit's own save confirms.
 *
 * This is plain (non-React) logic so the timing rules can be tested directly;
 * `useDebouncedRename` is the React binding.
 *
 * @param save Persists a rename. Only called for ids that already exist server
 *   side — a non-numeric id means the row has not been created yet.
 */
export function createRenameQueue(
  save: (id: number, name: string) => Promise<unknown>,
  debounceMs: number = RENAME_DEBOUNCE_MS,
): RenameQueue {
  const pending = new Map<RenameId, string>();
  const timers = new Map<RenameId, ReturnType<typeof setTimeout>>();

  const commit = async (id: number, name: string) => {
    try {
      await save(id, name);
    } catch (err) {
      console.error('Failed to rename:', err);
    } finally {
      // Keep shielding if the user typed again while this save was in flight —
      // that newer edit has its own save scheduled and must win.
      if (pending.get(id) === name) {
        pending.delete(id);
      }
    }
  };

  return {
    queue(id, name) {
      pending.set(id, name);

      // Rows that only exist locally are persisted by their own create call.
      if (typeof id !== 'number') return;

      const existing = timers.get(id);
      if (existing) clearTimeout(existing);

      timers.set(
        id,
        setTimeout(() => {
          timers.delete(id);
          void commit(id, name);
        }, debounceMs),
      );
    },

    resolve(id, serverName) {
      return pending.get(id) ?? serverName;
    },

    flush() {
      timers.forEach((timer, id) => {
        clearTimeout(timer);
        const name = pending.get(id);
        if (typeof id === 'number' && name !== undefined) {
          void commit(id, name);
        }
      });
      timers.clear();
    },
  };
}

export type DebouncedRename = Pick<RenameQueue, 'queue' | 'resolve'>;

/**
 * React binding for {@link createRenameQueue}. Holds one queue for the lifetime
 * of the component and flushes outstanding edits on unmount, so leaving the
 * page (e.g. advancing the setup wizard) mid-debounce still saves.
 */
export function useDebouncedRename(
  save: (id: number, name: string) => Promise<unknown>,
  debounceMs: number = RENAME_DEBOUNCE_MS,
): DebouncedRename {
  // Read through a ref so the queue never captures a stale mutation closure.
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  // Lazy initialiser: one queue for the component's whole lifetime, so a
  // re-render never drops timers that are already scheduled. `debounceMs` is
  // read once, at creation.
  // (react-hooks/refs warns here; the closure below is only invoked when a
  // debounced save fires, never during render.)
  const [renameQueue] = useState(() =>
    createRenameQueue((id, name) => saveRef.current(id, name), debounceMs),
  );

  useEffect(() => () => renameQueue.flush(), [renameQueue]);

  return renameQueue;
}

export default useDebouncedRename;
